import { useEffect, useRef } from "react";
import { useSubscriptionHistory } from "../hooks/useApi";
import Badge from "./Badge";
import { toPersianDigits } from "../utils/format";

interface SubscriptionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface HistoryItem {
  subscription_id: number;
  plan_name: string;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  plan_kind?: string;
}

function displayPlanName(name: string, planKind?: string): string {
  if (!name) return "رایگان";
  if (name.trim().toLowerCase() === "free") return "رایگان";
  return planKind === "fixed" ? toPersianDigits(name) : name;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("fa-IR");
}

export default function SubscriptionHistoryDrawer({ open, onClose }: SubscriptionHistoryDrawerProps) {
  const backdropMouseDown = useRef(false);
  const { data: history, isLoading } = useSubscriptionHistory(open);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const items: HistoryItem[] = history || [];

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in"
        onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
        onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) onClose(); backdropMouseDown.current = false; }}
      />

      {/* Drawer panel — slides in from the left, fills half the page width */}
      <aside className="absolute top-0 bottom-0 left-0 w-full md:w-1/2 bg-surface-base border-r border-line shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden animate-drawer-left">
        {/* Header */}
        <div className="bg-surface px-6 pt-5 pb-4 border-b border-line">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-h2 text-text-main">تاریخچه اشتراک</h2>
              <p className="text-small text-text-tertiary mt-1">سابقه پلن‌های اشتراک شما</p>
            </div>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-text-main transition-colors p-1 shrink-0"
              aria-label="بستن"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="space-y-3">
              <div className="h-10 bg-surface-hover rounded animate-pulse" />
              <div className="h-10 bg-surface-hover rounded animate-pulse" />
              <div className="h-10 bg-surface-hover rounded animate-pulse" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-body text-text-tertiary">هنوز اشتراکی ثبت نشده است</p>
            </div>
          ) : (
            <table className="w-full text-right">
              <thead>
                <tr className="border-b border-line">
                  <th className="pb-3 text-small font-medium text-text-secondary">تاریخ شروع</th>
                  <th className="pb-3 text-small font-medium text-text-secondary">تاریخ پایان</th>
                  <th className="pb-3 text-small font-medium text-text-secondary">پلن</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.subscription_id} className="border-b border-line last:border-b-0">
                    <td className="py-3.5 text-small text-text-main font-number whitespace-nowrap">
                      {formatDate(item.start_date)}
                    </td>
                    <td className="py-3.5 text-small text-text-main font-number whitespace-nowrap">
                      {item.end_date ? formatDate(item.end_date) : "—"}
                    </td>
                    <td className="py-3.5">
                      <span className="inline-flex items-center gap-2">
                        <span className="text-small text-text-main">{displayPlanName(item.plan_name, item.plan_kind)}</span>
                        {item.is_active && <Badge variant="success">فعال</Badge>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </aside>
    </div>
  );
}
